import { AbstractShapeModel } from 'flux-diagram-composer';
import { TiptapEditor } from '../../../framework/ui/components/tiptap/tiptap-editor.cmp';
import { CommandService, IModifier, StateService } from 'flux-core';
import { DiagramToViewportCoordinate } from '../../coordinate/diagram-to-viewport-coordinate.svc';
import { filter, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { DiagramLocatorLocator } from '../../diagram/locator/diagram-locator-locator';
import { AfterViewInit, OnDestroy, Component, ChangeDetectionStrategy, ComponentFactoryResolver,
    ViewChild, ViewContainerRef, Injector, ComponentRef, ElementRef } from '@angular/core';
import { of, Subscription, Observable } from 'rxjs';
import { Editor, getHTMLFromFragment } from '@tiptap/core';
import { TiptapHelper } from '../../../framework/ui/components/tiptap/tiptap-helper';
import { EDataLocatorLocator } from '../../edata/locator/edata-locator-locator';
import { ShapeModel } from '../../shape/model/shape.mdl';
import { splitModifier } from '../../edata/locator/edata-split-modifier';
import { getTextDirection } from '../../../framework/ui/components/tiptap/custom-extensions/text-direction';
import { AbstractTiptapDocumentsManager } from './abstract-tiptap-documents-manager';

// tslint:disable:member-ordering
/**
 * TiptapDocumentsManagerEdata
 *
 * This component creates a tiptap document per document as the TiptapDocument
 * state changes. TiptapDocument state can be set from any where in the app to
 * request this component to create a new tiptap doc and a `child editor node view` for the shapeId
 * e.g. { diagramId: string, shapeId?: string }
 *
 * FIXME:
 * Destruction of the tip editors is not done since thre's no exact point.
 * Please look into this further.
 * @author thisun
 * @since 2022 - 06 - 29
 */

@Component({
    template: `<div #viewContainer></div>`,
    selector: 'tiptap-documents-manager-edata',
    styleUrls: [ './tiptap-documents-manager-edata.cmp.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TiptapDocumentsManagerEdata extends AbstractTiptapDocumentsManager implements AfterViewInit, OnDestroy {

    public static documents: { [diagramId: string]: ComponentRef<TiptapEditor> } = {};

    protected subs: Subscription[] = [];

    @ViewChild( 'viewContainer', { read: ViewContainerRef, static: false })
    protected viewContainer: ViewContainerRef;

    constructor (
        protected componentFactoryResolver: ComponentFactoryResolver,
        protected injector: Injector,
        protected dtov: DiagramToViewportCoordinate,
        protected ll: DiagramLocatorLocator,
        protected ell: EDataLocatorLocator,
        protected er: ElementRef,
        protected commandSvc: CommandService,
        protected state: StateService<any, any> ) {
            super( componentFactoryResolver, injector, ll, commandSvc );
    }

    public getDocuments() {
        return TiptapDocumentsManagerEdata.documents;
    }

    public ngAfterViewInit() {
        this.subs.push(
            this.state.changes( 'CurrentDiagram' ).pipe(
                switchMap( diagramId => this.state.changes( 'TiptapDocument' ).pipe(
                    startWith({ diagramId }),
                    filter( data => data.diagramId && data.edataId && data.entityId ),
                    switchMap( data => this.manageDocument( data.edataId, data.diagramId, data.shapeId )),
                )),
            ).subscribe(),
            this.state.changes( 'CurrentDiagram' ).pipe(
                switchMap( diagramId => this.manageShapeDeletes()),
            ).subscribe(),
        );
    }

    public getEditor( edataId: string, diagramId?: string ): Observable<ComponentRef<TiptapEditor>> {
        const cmpRef = this.getDocuments()[ edataId ];
        if ( !cmpRef ) {
            return this.createTiptap( edataId, diagramId ).pipe(
                tap( cr => this.getDocuments()[ edataId ] = cr ),
            );
        }
        return of( cmpRef );
    }

    /**
     * Creates a tiptap editor for the diagram if not already created,
     * If the shapeId is given, adds a child editor node view for that shape
     * @param diagramId
     * @param shapeId
     */
    /**
     * Creates a tiptap editor for the diagram if not already created,
     * If the shapeId is given, adds a child editor node view for that shape
     * @param diagramId
     * @param shapeId
     */
     public manageDocument( edataId: string, diagramId: string, shapeId?: string ) {
        if ( !shapeId ) {
            return of({});
        }
        return this.getEditor( edataId, diagramId ).pipe( switchMap( cmpRef => {
            cmpRef.instance.shapeIdSubject.next( shapeId );
            const editor = cmpRef.instance.editorSubject.value;
            return this.ll.forDiagram( diagramId, false ).getDiagramOnce().pipe(
                switchMap( diagram => this.ell.getEDataModel( edataId ).pipe(
                    map( edata => ({ diagram, edata })),
                )),
                switchMap(({ diagram, edata }) => {
                    const shape = diagram.shapes[ shapeId ] as ShapeModel;
                    const entityId = shape.entityId;
                    // const entityModel = edata.entities[ entityId ] as EntityModel;

                    const initialized: HTMLElement = document.querySelector(
                        `tiptap-editor-component[id="${edataId}"].notesEdata div[data-type="tiptap-child-editor-node"][entityid="${ entityId }"]`,
                    );
                    const dataItems = diagram.getDataItemsOfShape( shape );
                    if ( !initialized && dataItems.description && typeof dataItems.description.value === 'string' ) {
                            const value = dataItems.description.value || '<p></p>';
                            const { content } = this.preparePrimaryTextNode( shape, editor );
                            editor.chain()
                                .focus()
                                .setTextSelection( editor.state.doc.content.size )
                                .insertContent( `<tiptap-child-editor-node entityid="${ entityId }" >${content}${ value }</tiptap-child-editor-node>` )
                                .blur()
                                .run();
                            this.cleanUpDocument( diagramId );
                            const { from } = TiptapDocumentsManagerEdata.getPrimaryTextNode( edataId, entityId );

                            setTimeout(() => {
                                if ( from !== undefined ) {
                                    editor.chain()
                                    .focus()
                                    .setTextSelection( from + 1 )
                                    .run();
                                }
                            }, 10 );
                    } else if ( initialized && !initialized.innerText.trim()) {
                        this.updatePrimaryTextNode( edataId, entityId, shape );
                        const { from } = TiptapDocumentsManagerEdata.getPrimaryTextNode( edataId, entityId );
                        if ( from !== undefined ) {
                            editor.commands.setTextSelection( from + 1 );
                        }
                    } else {
                        this.updatePrimaryTextNode( edataId, entityId, shape );
                    }
                  // NOTE: There's a chance of getting text editor nodes duplicated for a single
                    // shape when collab editing. ( e.g. 2 collabs try to add notes to the
                    // same shape at the same time ) This deletes the most recent duplicate.
                    setTimeout(() => {
                        TiptapDocumentsManagerEdata.removeDuplicates( edataId, entityId );
                    }, 1000 );
                    return of({});
                }),
            );
        }));
    }

    /**
     * This method prpends a new tiptap primary text node into the child text editor node view
     * if such node is not present
     */
    protected insertPrimaryTextNode( edataId: string, entityId: string, shapeModel ) {
        const { domNode, from, editor } = TiptapDocumentsManagerEdata.getChildEditorNode( edataId, entityId );
        if ( !domNode || !from || !editor ) {
            return;
        }
        const { content } = this.preparePrimaryTextNode( shapeModel, editor );
        if (( domNode as any )?.firstChild?.firstChild?.getAttribute( 'data-type' ) !==  'primaryTextNode' ) {
            editor.chain()
                .focus()
                .setTextSelection( from )
                .insertContent( content )
                .blur()
                .run();
        }
    }

    protected updatePrimaryTextNode( edataId, entityId, shapeModel ) {
        const { domNode, from, editor } = TiptapDocumentsManagerEdata.getChildEditorNode( edataId, entityId );
        const textModel = shapeModel.primaryTextModel;
        if ( !textModel ) {
            return;
        }
        if (( domNode as any )?.firstChild?.firstChild?.getAttribute( 'data-type' ) ===  'primaryTextNode' ) {
            const { details, content } = this.preparePrimaryTextNode( shapeModel, editor );
            const val = TiptapDocumentsManagerEdata.getPrimaryTextNode( edataId, entityId );
            const text = val.domNode.querySelector( 'summary' ).innerText;
            if (( textModel.plainText && text !== textModel.plainText ) || details !== '<p></p>' ) {
                editor.chain()
                    .focus()
                    .setTextSelection( from + 1 )
                    .deleteNode( 'primaryTextNode' ).run();
                editor.chain().setTextSelection( from )
                    .insertContent( content )
                    .blur()
                    .run();
            }
        } else {
            this.insertPrimaryTextNode( edataId, entityId, shapeModel );
        }
    }


    protected preparePrimaryTextNode( shapeModel: AbstractShapeModel, editor ) {
        // Create new node
        let title = '';
        let details = '';
        let editable = true;
        const textModel = shapeModel.primaryTextModel;
        const hbText = ( textModel as any ).handlebarsReplacedText;
        if ( hbText ) {
            details = TiptapHelper
                    .convertCarotaToTiptapHtml( textModel.content, editor.schema );
            editable = false;
            title = hbText;
        } else if ( textModel && textModel.rendering === 'tiptapCanvas' ) {
            if ( !TiptapHelper.isUniformFormatWithNoLineBreaks( textModel.html )) {
                details = textModel.html;
                editable = false;
            }
            title = textModel?.plainText || '';
        } else {
            if ( textModel?.content.length > 1 ) {
                title = textModel.plainText;
                details = TiptapHelper
                    .convertCarotaToTiptapHtml( textModel.content, editor.schema );
                editable = false;
            }
            title = textModel?.plainText || '';
        }
        if ( !details ) {
            details = '<p></p>';
        }
        const titleNode = document.createTextNode( title );
        const detailsNode = document.createElement( 'div' );
        detailsNode.innerHTML = details;

        const primaryTextNode = document.createElement( 'primary-text-node' );
        primaryTextNode.setAttribute( 'showbutton', `${!editable}` );

        const summary = document.createElement( 'summary' );
        const dir = getTextDirection( title ) || 'ltr'  ;
        summary.setAttribute( 'inactive', `${!editable}` );
        summary.setAttribute( 'dir', dir );
        summary.setAttribute( 'contenteditable', `${editable}` );
        summary.setAttribute( 'contenteditable', `${editable}` );
        summary.appendChild( titleNode );

        primaryTextNode.appendChild( summary );
        primaryTextNode.appendChild( detailsNode );


        return {
            content: primaryTextNode.outerHTML,
            title,
            details,
            editable,
        };
    }


    public removeShapeTextNodes( edataId, entityId ) {
        const elements = document.querySelectorAll(
            `tiptap-editor-component[id="${edataId}"].notesEdata div[data-type="tiptap-child-editor-node"][entityid="${ entityId }"]` ) || [];
        elements.forEach(( el: HTMLElement ) => el.remove());
    }

    public cleanUpTextNodes( edataId, entityIds: string[]) {
        const elements = document.querySelectorAll(
            `tiptap-editor-component[id="${edataId}"].notesEdata div[data-type="tiptap-child-editor-node"]` ) || [];
        elements.forEach(( el: HTMLElement ) => {
            if ( !entityIds.includes( el.getAttribute( 'shapeId' ))) {
                el.remove();
            }
        });
    }


    /**
     * After executing this method, only tiptap child editor nodes are left in the document.
     * @param diagramId
     * @param shapeIds
     */
    protected cleanUpDocument( edataId ) {
        const elements = document.querySelectorAll(
            `tiptap-editor-component[id="${edataId}"].notesEdata .prose > *` ) || [];
        elements.forEach(( el: HTMLElement ) => {
            if ( !el.classList.contains( 'tiptap-child-editor' )) {
                el.remove();
            }
        });
    }

    public static removeDuplicates( edataId, entityId ) {
        const elements = document.querySelectorAll(
            `tiptap-editor-component[id="${edataId}"].notesEdata div[data-type="tiptap-child-editor-node"][entityid="${ entityId }"]` ) || [];
        elements.forEach(( el: HTMLElement, i ) => {
            if ( i > 0 ) {
                el.remove();
            }
        });

        const primaryTextNodes = document.querySelectorAll(
            `tiptap-editor-component[id="${edataId}"].notesEdata div[data-type="tiptap-child-editor-node"][entityid="${ entityId }"] div[data-type="primaryTextNode"]` ) || [];
        primaryTextNodes.forEach(( el: HTMLElement, i ) => {
            if ( i > 0 ) {
                el.remove();
            }
        });
    }

    /**
     * Destroys the toolbar
     */
    public ngOnDestroy() {
        while ( this.subs.length > 0 ) {
            this.subs.pop().unsubscribe();
        }
    }


    /**
     * Create RichTextEditorUIC component and set initial value
     */
    public createTiptap( edataId: string, diagramId?: string ): Observable<ComponentRef<TiptapEditor>> {
        const itemRefTiptap: ComponentRef<TiptapEditor> = this.makeComponent( TiptapEditor );
        const tipTapEditorCmp = itemRefTiptap.instance;
        tipTapEditorCmp.id = 'notesEdata';
        tipTapEditorCmp.diagramId = diagramId;
        tipTapEditorCmp.edataId = edataId;
        tipTapEditorCmp.transformPastedHTML = v => this.transformPastedHTML( v );
        this.insert( this.viewContainer, itemRefTiptap );
        itemRefTiptap.changeDetectorRef.detectChanges();
        this.subs.push( this.escapeSub( tipTapEditorCmp ));
        const sub = tipTapEditorCmp.change.subscribe( data => {
            this.onChildNodeviewChange( diagramId, {
                shapeId: tipTapEditorCmp.shapeIdSubject.value,
                html: data.html,
            });
        });
        this.subs.push( sub );
        return itemRefTiptap.instance.hpSynced.pipe(
            filter( v => !!v ),
            switchMap(() => itemRefTiptap.instance.editorSubject ),
            filter( v => !!v ),
            take( 1 ),
            map(() => itemRefTiptap ),
        );
    }


    protected transformPastedHTML( html: string ) {
        const el = document.createElement( 'div' );
        el.innerHTML = html;
        const elements = el.querySelectorAll( 'div[data-type="detailsContent"]' );
        elements.forEach( elm => {
            elm.removeAttribute( 'data-type' );
            const innerElements = elm.querySelectorAll( '*[style]' );
            innerElements.forEach(( innerElement: any ) => {
              innerElement.style.removeProperty( 'text-align' );
            });
        });
        return el.innerHTML;
    }


    /**
     * Updates the tiptap editor for notes sections corresponds to the given modifier
     * @param change
     */
    public static updateDocumentByModifier( edataId: string, c: IModifier ) {
        // TODO: Show conflicts resolver
        const split = splitModifier( c );
        Object.keys( split.entities || {})
            .forEach( entityId => {
                if ( Object.keys( split.entities[ entityId ].$set || {}).includes( 'data.description' )) {
                    const value = split.entities[ entityId ].$set[ `data.description` ];
                    TiptapDocumentsManagerEdata.updateDocument( edataId, entityId, value );
                }
            });
    }

    /**
     * Replaces the notes section by given html content
     * @param change
     */
     public static updateDocument( edataId: string, entityId: string, html: string ) {
        const { node, from, to, editor } = TiptapDocumentsManagerEdata
            .getChildEditorNode( edataId, entityId );
        if (  editor ) {
            const current = getHTMLFromFragment( node.content, editor.schema );
            if ( html !== current ) {
                editor.chain()
                .focus()
                .insertContentAt({ from, to }, html )
                .blur()
                .run();
            }
        }
    }

    public static getChildEditorNode( edataId: string, entityId: string ):
    { domNode: HTMLElement, node?: any, from?: number, to?: number, editor?: Editor } {
        const editor = TiptapDocumentsManagerEdata.documents[ edataId ]?.instance?.editor;
        if ( !editor ) {
            return { domNode: null };
        }
        const domNode: HTMLElement = editor.view.dom.querySelector(
            `tiptap-editor-component[id="${ edataId }"].notesEdata div[data-type="tiptap-child-editor-node"][entityid="${ entityId }"]`,
        );
        if ( domNode ) {
            // const editor = TiptapDocumentsManagerEdataShapeText.documents[ diagramId ].instance.editor;
            const pos = editor.view.posAtDOM( domNode as HTMLElement, 0 );
            const $pos = editor.view.state.doc.resolve( pos );
            for ( let d = $pos.depth; d > 0; d-- ) {
                const node = $pos.node( d );
                if ( node.type.name === 'tiptapChildEditor' ) {
                    return { domNode, node, from: pos, to: pos + node.nodeSize - 3, editor };
                }
            }
        }
        return { domNode };
    }

    public static getPrimaryTextNode( edataId: string, entityId: string ):
    { domNode: HTMLElement, node?: any,
        from?: number, to?: number, editor?: Editor, title?: string, details?: string } {
        const editor = TiptapDocumentsManagerEdata.documents[ edataId ].instance.editor;
        const domNode: HTMLElement = editor.view.dom.querySelector(
            `tiptap-editor-component[id="${ edataId }"].notesEdata div[data-type="tiptap-child-editor-node"][entityid="${ entityId }"] div[data-type="primaryTextNode"]`,
        );
        if ( domNode ) {
            // const editor = TiptapDocumentsManagerEdataShapeText.documents[ diagramId ].instance.editor;
            const pos = editor.view.posAtDOM( domNode as HTMLElement, 0 );
            const $pos = editor.view.state.doc.resolve( pos );
            for ( let d = $pos.depth; d > 0; d-- ) {
                const node = $pos.node( d );
                if ( node.type.name === 'primaryTextNode' ) {
                    const title = domNode.querySelector( 'summary' ).textContent;
                    const detailsElement = domNode.querySelector( 'div[data-type="detailsContent"]' );
                    let details = '';
                    if ( detailsElement.textContent.trim()) {
                        details = domNode.querySelector( 'div[data-type="detailsContent"]' ).innerHTML;
                    }
                    return { domNode, node, from: pos, to: pos + node.nodeSize - 3, editor, title, details };
                }
            }
        }
        return { domNode };
    }

}
